/**
 * 广场多音源搜索结果的封面补全工具。
 * 各平台 API 与 MusicFree / 洛雪 插件 JSON 并不相同，此处只处理本项目 pluginClient 实际使用的接口。
 */

import { requestViaBackendProxy, postViaBackendProxy } from './musicPluginProxy.js';

const WY_HEADERS = {
  Referer: 'https://music.163.com/',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

const KW_HEADERS = {
  Referer: 'https://www.kuwo.cn/',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

const KG_HEADERS = {
  Referer: 'https://www.kugou.com/',
  'User-Agent': 'KuGou2012-9020-ExpandSearchManager',
  'KG-RC': '1',
  'KG-THash': 'expand_search_manager.cpp:852736169:451'
};

/** 小窝源（酷我）封面统一使用的高清尺寸 */
export const KW_COVER_HD_SIZE = 500;

function isValidKwCoverUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  return /kuwo\.cn/i.test(trimmed);
}

/** img*.kuwo.cn 支持 HTTPS；kwcdn 等域名常仅提供 HTTP */
export function normalizeKwCoverUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^http:\/\/img\d+\.kuwo\.cn\//i.test(trimmed)) {
    return `https://${trimmed.slice(7)}`;
  }
  return trimmed;
}

/**
 * HTTPS 页面下，酷我 HTTP 封面需走后端代理，否则 iOS Safari 会因混合内容拦截。
 */
export function wrapKwCoverForDisplay(coverUrl) {
  if (!coverUrl || typeof coverUrl !== 'string') return coverUrl;
  const normalized = normalizeKwCoverUrl(coverUrl.trim());
  if (!normalized) return normalized;

  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    normalized.startsWith('http://') &&
    /kuwo\.cn/i.test(normalized)
  ) {
    return `/api/cover/proxy/fetch?url=${encodeURIComponent(normalized)}`;
  }

  return normalized;
}

/**
 * 从封面代理 URL 中递归解包出最内层原始地址（避免保存时双重 /api/cover/proxy 嵌套）。
 */
export function unwrapCoverProxyUrl(url) {
  if (!url || typeof url !== 'string') return url;
  let current = url.trim();
  let guard = 0;

  while (guard++ < 5 && current.includes('/api/cover/proxy/')) {
    try {
      const base =
        typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
      const parsed = new URL(current, base);
      if (parsed.pathname.endsWith('/api/cover/proxy/fetch')) {
        const inner = parsed.searchParams.get('url');
        if (!inner) break;
        current = inner;
        continue;
      }
    } catch {
      // fall through to regex
    }

    const match = current.match(/\/api\/cover\/proxy\/fetch\?url=([^&]+)/i);
    if (!match) break;
    try {
      current = decodeURIComponent(match[1]);
    } catch {
      break;
    }
  }

  return current;
}

/** 广场插件搜索结果封面：酷我 HTTP 走代理，其余外链原样展示 */
export function resolveExternalCoverForDisplay(coverUrl, song = null) {
  if (!coverUrl || typeof coverUrl !== 'string') return coverUrl;
  const trimmed = coverUrl.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith('/api/cover/proxy/')) return trimmed;
  if (/kuwo\.cn/i.test(trimmed) || song?.source === 'kw-plugin') {
    return wrapKwCoverForDisplay(trimmed);
  }
  return trimmed;
}

export function stripSearchHighlightTags(text) {
  if (!text || typeof text !== 'string') return text;
  const withoutTags = text.replace(/<\/?em>/gi, '').replace(/<[^>]+>/g, '');
  return withoutTags.trim();
}

export function resolveWyCoverFromItem(item) {
  if (!item) return null;
  return (
    item.al?.picUrl ||
    item.album?.picUrl ||
    item.album?.blurPicUrl ||
    item.picUrl ||
    null
  );
}

export function resolveWyCoverFromDetailSong(song) {
  if (!song) return null;
  return (
    song.al?.picUrl ||
    song.album?.picUrl ||
    song.album?.blurPicUrl ||
    null
  );
}

/**
 * 酷我 web_albumpic_short 形如 "120/s3s94/93/211513640.jpg"
 * 完整路径需保留前缀，img 子域由首位数字决定；尺寸段统一升级为高清档。
 */
export function artworkShort2Long(shortPic, size = KW_COVER_HD_SIZE) {
  if (!shortPic) return null;
  if (/^https?:\/\//i.test(shortPic)) return upgradeKwCoverUrl(shortPic, size);
  const server = shortPic.charAt(0);
  if (!/\d/.test(server)) return null;
  const hdShortPic = shortPic.replace(/^(\d+)\//, `${size}/`);
  return `https://img${server}.kuwo.cn/star/albumcover/${hdShortPic}`;
}

export function isKwLowResCoverUrl(coverUrl) {
  if (!coverUrl || typeof coverUrl !== 'string') return false;
  const match = coverUrl.match(/img\d+\.kuwo\.cn\/star\/albumcover\/(\d+)\//i);
  if (!match) return false;
  const numericSize = Number(match[1]);
  return !Number.isNaN(numericSize) && numericSize < KW_COVER_HD_SIZE;
}

/** 将酷我 albumcover 路径中的尺寸段（如 120/）升级为 500/ 等高清尺寸 */
export function upgradeKwCoverUrl(coverUrl, size = KW_COVER_HD_SIZE) {
  if (!coverUrl || typeof coverUrl !== 'string') return coverUrl;
  const trimmed = coverUrl.trim();
  if (!trimmed) return trimmed;

  if (/img\d+\.kuwo\.cn\/star\/albumcover\/\d+\//i.test(trimmed)) {
    return trimmed.replace(/(img\d+\.kuwo\.cn\/star\/albumcover\/)(\d+)(\/)/i, (full, prefix, currentSize, slash) => {
      const numericSize = Number(currentSize);
      if (!Number.isNaN(numericSize) && numericSize >= size) {
        return full;
      }
      return `${prefix}${size}${slash}`;
    });
  }

  return trimmed;
}

export function resolveKwCoverFromItem(item) {
  if (!item) return null;
  const cover =
    artworkShort2Long(item.web_albumpic_short) ||
    item.web_albumpic ||
    item.pic ||
    item.img ||
    null;
  return resolveExternalCoverForDisplay(upgradeKwCoverUrl(cover), { source: 'kw-plugin' });
}

export function resolveKugouImageUrl(image, imgsize) {
  if (!image || typeof image !== 'string') return null;
  if (!image.includes('{size}')) return image;
  const size = Array.isArray(imgsize) && imgsize[0] ? String(imgsize[0]) : '400';
  return image.replace('{size}', size);
}

export function resolveKgCoverFromItem(item) {
  if (!item) return null;
  return resolveKugouImageUrl(item.Image || item.img || item.album_img, item.imgsize);
}

/** 咪咕音乐 imgItems 按 01/02/03 由小到大排列，取最后一项作为高清封面 */
export function resolveMgCoverFromItem(item) {
  if (!item) return null;
  const imgItems = Array.isArray(item.imgItems) ? item.imgItems : [];
  const best = imgItems[imgItems.length - 1] || imgItems[0];
  return best?.img || item.img || null;
}

export async function enrichWySearchCovers(songs) {
  const pending = songs.filter((song) => !song.coverUrl && song.id);
  if (pending.length === 0) return songs;

  const ids = pending.map((song) => song.id).slice(0, 50);
  const idsParam = encodeURIComponent(`[${ids.join(',')}]`);
  const url = `https://music.163.com/api/song/detail/?ids=${idsParam}`;

  try {
    const body = await requestViaBackendProxy(url, WY_HEADERS);
    const coverById = new Map();
    for (const detailSong of body?.songs || []) {
      const cover = resolveWyCoverFromDetailSong(detailSong);
      if (cover) {
        coverById.set(String(detailSong.id), cover);
      }
    }

    for (const song of songs) {
      if (song.coverUrl) continue;
      const cover = coverById.get(String(song.id));
      if (cover) {
        song.coverUrl = cover;
        song.artwork = cover;
      }
    }
  } catch (error) {
    console.warn('[plazaCoverUtils] 网易封面补全失败:', error.message);
  }

  return songs;
}

async function fetchKwCoverByRid(rid, size = KW_COVER_HD_SIZE) {
  const url = `http://artistpicserver.kuwo.cn/pic.web?corp=kuwo&type=rid_pic&pictype=${size}&size=${size}&rid=${encodeURIComponent(rid)}`;
  const body = await requestViaBackendProxy(url, KW_HEADERS);
  const text = typeof body === 'string' ? body.trim() : '';
  return isValidKwCoverUrl(text) ? text : null;
}

/** 小窝源封面：优先 rid 高清接口，失败则升级 albumcover 路径尺寸（保留原始 URL 作兜底） */
export async function resolveKwHdCoverUrl(coverUrl, songId) {
  const original = normalizeKwCoverUrl(coverUrl);
  let best = original || null;

  if (songId) {
    try {
      const hdCover = await fetchKwCoverByRid(songId);
      if (hdCover) {
        return normalizeKwCoverUrl(hdCover) || hdCover;
      }
    } catch {
      // 单条失败走路径升级
    }
  }

  const upgraded = upgradeKwCoverUrl(original);
  if (upgraded && isValidKwCoverUrl(upgraded)) {
    best = upgraded;
  }

  return best;
}

export async function enrichKwSearchCovers(songs) {
  const pending = songs.filter((song) => song.id);
  if (pending.length === 0) return songs;

  await Promise.all(
    pending.slice(0, 30).map(async (song) => {
      try {
        const cover = await resolveKwHdCoverUrl(song.coverUrl || song.artwork, song.id);
        if (cover) {
          song._originalCoverUrl = cover;
          const displayCover = wrapKwCoverForDisplay(cover);
          song.coverUrl = displayCover;
          song.artwork = displayCover;
        }
      } catch {
        const fallback = upgradeKwCoverUrl(song.coverUrl || song.artwork);
        if (fallback) {
          song._originalCoverUrl = fallback;
          const displayCover = wrapKwCoverForDisplay(fallback);
          song.coverUrl = displayCover;
          song.artwork = displayCover;
        }
      }
    })
  );

  return songs;
}

function buildKugouPrivilegePayload(song) {
  const raw = song.raw || {};
  const hash = song.id || raw.FileHash || raw.hash || raw.HASH;
  const albumId = raw.AlbumID || raw.album_id || song.albumId;
  const albumAudioId = raw.Audioid || raw.album_audio_id || song.albumAudioId;
  const artist = song.artist || raw.SingerName || '未知艺术家';
  const title = song.title || raw.SongName || '未知歌曲';

  if (!hash) return null;

  let resolvedAlbumAudioId = albumAudioId;
  if (resolvedAlbumAudioId && String(resolvedAlbumAudioId).length === 32 && raw.audioId) {
    resolvedAlbumAudioId = String(raw.audioId).split('_')[0];
  }

  return {
    appid: 1001,
    area_code: '1',
    behavior: 'play',
    clientver: '9020',
    need_hash_offset: 1,
    relate: 1,
    resource: [
      {
        album_audio_id: resolvedAlbumAudioId || hash,
        album_id: albumId,
        hash,
        id: 0,
        name: `${artist} - ${title}.mp3`,
        type: 'audio'
      }
    ],
    token: '',
    userid: 2626431536,
    vip: 1
  };
}

async function fetchKugouCover(song) {
  const payload = buildKugouPrivilegePayload(song);
  if (!payload) return null;

  const body = await postViaBackendProxy(
    'http://media.store.kugou.com/v1/get_res_privilege',
    JSON.stringify(payload),
    KG_HEADERS,
    { contentType: 'application/json' }
  );

  if (body?.error_code !== 0 || !Array.isArray(body?.data) || !body.data[0]?.info) {
    return null;
  }

  const info = body.data[0].info;
  const image = info.image || info.img || null;
  return resolveKugouImageUrl(image, info.imgsize);
}

export async function enrichKgSearchCovers(songs) {
  const pending = songs.filter((song) => !song.coverUrl);
  if (pending.length === 0) return songs;

  await Promise.all(
    pending.slice(0, 20).map(async (song) => {
      try {
        const cover = await fetchKugouCover(song);
        if (cover) {
          song.coverUrl = cover;
          song.artwork = cover;
        }
      } catch {
        // 单条失败不影响列表
      }
    })
  );

  return songs;
}
